import React, { useState, useEffect } from 'react'

interface ServicePurchaseFormProps {
  title: string
  serviceProviders: Array<{ id: string; name: string }>
  expenseArticles: Array<{ id: string; code: string; name: string; type: string }>
  initial?: any
  onCancel: () => void
  onSubmit: (data: any) => void
}

const ServicePurchaseForm: React.FC<ServicePurchaseFormProps> = ({
  title,
  serviceProviders,
  expenseArticles,
  initial,
  onCancel,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    date: initial?.date || new Date().toISOString().split('T')[0],
    serviceProviderId: initial?.serviceProviderId || '',
    status: initial?.status || 'DRAFT',
    comment: initial?.comment || '',
    expenseArticleId: initial?.expenseArticleId || undefined,
    items: initial?.items || []
  })

  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Ошибка сохранения:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Поставщик услуг
              </label>
              <select
                value={formData.serviceProviderId}
                onChange={(e) => setFormData({ ...formData, serviceProviderId: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">Выберите поставщика</option>
                {serviceProviders.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Статус
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="DRAFT">Черновик</option>
                <option value="POSTED">Проведен</option>
                <option value="CANCELLED">Отменен</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Статья затрат
              </label>
              <select
                value={formData.expenseArticleId || ''}
                onChange={(e) => setFormData({ ...formData, expenseArticleId: e.target.value || undefined })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Выберите статью затрат</option>
                {expenseArticles
                  .filter(article => article.type === 'EXPENSE')
                  .map(article => (
                    <option key={article.id} value={article.id}>
                      {article.code} - {article.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Комментарий
            </label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
            />
          </div>

          {/* Позиции услуг */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Позиции услуг
            </label>
            <div className="space-y-2">
              {formData.items.map((item: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-md p-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="font-medium">Турнир:</span> {item.tournament?.name || '-'}
                    </div>
                    <div>
                      <span className="font-medium">Корт:</span> {item.court?.name || '-'}
                    </div>
                    <div>
                      <span className="font-medium">Время:</span> {item.startTime} - {item.endTime}
                    </div>
                    <div>
                      <span className="font-medium">Часов:</span> {item.hours}
                    </div>
                    <div>
                      <span className="font-medium">Ставка/час:</span> {item.ratePerHour?.toFixed(2) || '0.00'}
                    </div>
                    <div>
                      <span className="font-medium">Сумма:</span> {item.amount?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  {item.comment && (
                    <div className="mt-2 text-sm text-gray-600">
                      <span className="font-medium">Комментарий:</span> {item.comment}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ServicePurchaseForm
